import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getCampaign, getCampaignAssets, generateAssetsWithAI, personalizeAsset, generateWithAIPrompt, getCampaignAssetVersions, markAssetUsed } from '../utils/api';
import { CAMPAIGN_DETAIL } from '../constants/testIds';
import { ArrowLeft, FileText, Image, Download, ExternalLink, Wrench as WrenchTool, CheckSquare, FileArchive, Copy, Mail, ExternalLinkIcon, Sparkles, BookOpen } from 'lucide-react';
import Layout from '../components/Layout';
import { CampaignKPICard } from '../components/CampaignKPICard';
import { CampaignGoalPanel } from '../components/CampaignGoalPanel';
import { PageFlowGuide } from '../components/PageFlowGuide';
import { AICustomizationCenter } from '../components/AICustomizationCenter';
import { TrackingPackagePanel } from '../components/TrackingPackagePanel';
import { RewriteMenu, STATUS_STYLES, STATUS_LABELS } from '../components/RewriteMenu';
import { BrandAppliedBadge } from '../components/BrandAppliedBadge';
import { useAuth } from '../contexts/AuthContext';
import { tradeColor } from '../utils/tradeColors';
import { CampaignNumbersCard } from '../components/CampaignNumbersCard';
import { HelpTooltip } from '../components/HelpTooltip';
import JSZip from 'jszip';

const CampaignDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [goalData, setGoalData] = useState(null);
  const [versions, setVersions] = useState({});
  const [stackFilter, setStackFilter] = useState(false);
  const [highlightAsset, setHighlightAsset] = useState(null);
  const [assets, setAssets] = useState([]);
  const [usageGuide, setUsageGuide] = useState(null);
  const [showUsageGuide, setShowUsageGuide] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedAssetType, setSelectedAssetType] = useState('All');
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [selectedAssetTypes, setSelectedAssetTypes] = useState([]);
  const [showPersonalizeModal, setShowPersonalizeModal] = useState(false);
  const [selectedAssetForPersonalize, setSelectedAssetForPersonalize] = useState(null);
  const [personalizing, setPersonalizing] = useState(false);
  const [personalizedContent, setPersonalizedContent] = useState('');
  const [personalizeForm, setPersonalizeForm] = useState({
    company_name: '',
    city: '',
    state: '',
    phone: '',
    website: '',
    tone: 'professional',
    additional_context: ''
  });
  const [showAIPromptModal, setShowAIPromptModal] = useState(false);
  const [selectedAIPromptAsset, setSelectedAIPromptAsset] = useState(null);
  const [aiPromptGenerating, setAIPromptGenerating] = useState(false);
  const [generatedAIContent, setGeneratedAIContent] = useState('');
  const [aiPromptInputs, setAIPromptInputs] = useState({
    context: '',
    specific_details: ''
  });
  const [showWriteWithAI, setShowWriteWithAI] = useState(false);
  const [writeAIAsset, setWriteAIAsset] = useState(null);
  const [writeAIGenerating, setWriteAIGenerating] = useState(false);
  const [writeAIForm, setWriteAIForm] = useState({
    company_name: '',
    city: '',
    state: '',
    phone: '',
    service_area: '',
    custom_requirements: ''
  });

  useEffect(() => {
    loadCampaignData();
  }, [id]);

  useEffect(() => {
    if (!loading && window.location.hash === '#recommended-plan') {
      setTimeout(() => document.getElementById('recommended-plan')?.scrollIntoView({ behavior: 'smooth' }), 300);
    }
  }, [loading]);

  const loadCampaignData = async () => {
    try {
      const [campaignRes, assetsRes, versionsRes] = await Promise.all([
        getCampaign(id),
        getCampaignAssets(id),
        getCampaignAssetVersions(id).catch(() => ({ data: {} })),
      ]);
      setCampaign(campaignRes.data);
      setVersions(versionsRes.data || {});
      
      // Separate Usage Guide from regular assets
      const allAssets = assetsRes.data;
      const guide = allAssets.find(a => a.asset_type === 'Asset Usage Guide');
      const regularAssets = allAssets.filter(a => a.asset_type !== 'Asset Usage Guide');
      
      setUsageGuide(guide);
      setAssets(regularAssets);
    } catch (error) {
      console.error('Error loading campaign:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshVersions = async () => {
    try {
      const { data } = await getCampaignAssetVersions(id);
      setVersions(data || {});
    } catch (e) {
      console.error('versions refresh failed', e);
    }
  };

  const activeContentFor = (asset) => {
    const v = versions[asset.id];
    if (v?.active_version === 'edited' && v.edited_content) return v.edited_content;
    if (v?.active_version === 'ai_draft' && v.ai_draft_content) return v.ai_draft_content;
    return asset.content || asset.body || '';
  };

  const handleAIGeneration = async () => {
    if (selectedAssetTypes.length === 0) {
      alert('Please select at least one asset type to generate');
      return;
    }

    setAiGenerating(true);
    try {
      await generateAssetsWithAI(id, selectedAssetTypes);
      setShowAIModal(false);
      setSelectedAssetTypes([]);
      await loadCampaignData(); // Reload assets
      alert(`Successfully generated ${selectedAssetTypes.length} assets!`);
    } catch (error) {
      console.error('Error generating assets:', error);
      alert('Error generating assets. Please try again.');
    } finally {
      setAiGenerating(false);
    }
  };

  const toggleAssetType = (assetType) => {
    setSelectedAssetTypes(prev =>
      prev.includes(assetType)
        ? prev.filter(t => t !== assetType)
        : [...prev, assetType]
    );
  };

  const handleDownloadAsset = (asset) => {
    const content = activeContentFor(asset);
    markAssetUsed(asset.id).then(refreshVersions).catch(() => {});
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const assetName = asset.asset_name || asset.asset_type || 'asset';
    a.download = `${assetName.replace(/\s+/g, '_')}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleDownloadAllAsZip = async () => {
    if (assets.length === 0) {
      alert('No assets available to download');
      return;
    }

    try {
      const zip = new JSZip();
      const campaignName = campaign?.title || campaign?.campaign_name || 'campaign';
      const folderName = campaignName.replace(/[^a-z0-9]/gi, '_');
      
      // Group assets by category
      const categories = {};
      assets.forEach(asset => {
        const category = asset.category || 'Other';
        if (!categories[category]) {
          categories[category] = [];
        }
        categories[category].push(asset);
      });

      // Add assets to zip organized by category
      Object.entries(categories).forEach(([category, categoryAssets]) => {
        const categoryFolder = zip.folder(`${folderName}/${category}`);
        
        categoryAssets.forEach((asset, index) => {
          const content = asset.content || asset.body || '';
          const assetName = asset.asset_name || asset.asset_type || `asset_${index + 1}`;
          const fileName = `${assetName.replace(/[^a-z0-9]/gi, '_')}.txt`;
          categoryFolder.file(fileName, content);
        });
      });

      // Add campaign info file
      const campaignInfo = `Campaign: ${campaignName}
Trade: ${campaign?.trade || 'N/A'}
Problem: ${campaign?.problem || 'N/A'}
Primary Service: ${campaign?.primary_service || 'N/A'}
Seasonality: ${campaign?.seasonality || 'N/A'}
Urgency: ${campaign?.urgency || 'N/A'}

Total Assets: ${assets.length}
Generated: ${new Date().toLocaleDateString()}
`;
      zip.file(`${folderName}/CAMPAIGN_INFO.txt`, campaignInfo);

      // Generate and download zip
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${folderName}_Complete_Campaign.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
    } catch (error) {
      console.error('Error creating ZIP:', error);
      alert('Error creating download. Please try again.');
    }
  };

  const handleDownloadAll = () => {
    // Legacy function - now redirects to ZIP download
    handleDownloadAllAsZip();
  };

  // ========== TOOL HANDOFF FUNCTIONS ==========
  
  const handleCopyToClipboard = async (asset) => {
    const content = asset.content || asset.body || '';
    try {
      await navigator.clipboard.writeText(content);
      alert('✅ Copied to clipboard!');
    } catch (err) {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = content;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert('✅ Copied to clipboard!');
    }
  };

  const handleOpenInChatGPT = async (asset) => {
    const content = asset.content || asset.body || '';
    await navigator.clipboard.writeText(content);
    window.open('https://chat.openai.com', '_blank');
    setTimeout(() => {
      alert('📋 Prompt copied to clipboard!\n\nPaste it in ChatGPT to use this template.');
    }, 500);
  };

  const handleOpenInClaude = async (asset) => {
    const content = asset.content || asset.body || '';
    await navigator.clipboard.writeText(content);
    window.open('https://claude.ai', '_blank');
    setTimeout(() => {
      alert('📋 Prompt copied to clipboard!\n\nPaste it in Claude to use this template.');
    }, 500);
  };

  const handleOpenInCanva = async (asset) => {
    const content = asset.content || asset.body || '';
    await navigator.clipboard.writeText(content);
    window.open('https://www.canva.com', '_blank');
    setTimeout(() => {
      alert('📋 Design brief copied to clipboard!\n\nCreate your design in Canva using this brief.');
    }, 500);
  };

  const handleSendToEmail = (asset) => {
    const content = asset.content || asset.body || '';
    
    // Extract subject from content if it exists
    let subject = 'Marketing Campaign Asset';
    let body = content;
    
    // Try to extract subject line from email content
    if (content.includes('Subject:') || content.includes('subject:')) {
      const subjectMatch = content.match(/subject[:\s]+(.+)/i);
      if (subjectMatch && subjectMatch[1]) {
        subject = subjectMatch[1].split('\n')[0].trim();
        // Remove subject from body
        body = content.replace(/subject[:\s]+.+/i, '').trim();
      }
    }
    
    // Create mailto link
    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  // ========== END TOOL HANDOFF FUNCTIONS ==========

  // ========== AI PERSONALIZATION FUNCTIONS ==========
  
  const handleOpenPersonalizeModal = (asset) => {
    setSelectedAssetForPersonalize(asset);
    setPersonalizedContent('');
    setShowPersonalizeModal(true);
  };

  const handlePersonalize = async () => {
    if (!personalizeForm.company_name || !personalizeForm.city) {
      alert('Please fill in at least Company Name and City');
      return;
    }

    setPersonalizing(true);
    try {
      const response = await personalizeAsset(selectedAssetForPersonalize.id, personalizeForm);
      setPersonalizedContent(response.data.personalized_content);
      alert('✅ Content personalized successfully!');
    } catch (error) {
      console.error('Error personalizing asset:', error);
      alert('Error personalizing content. Please try again.');
    } finally {
      setPersonalizing(false);
    }
  };

  const handleCopyPersonalized = async () => {
    try {
      await navigator.clipboard.writeText(personalizedContent);
      alert('✅ Personalized content copied to clipboard!');
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = personalizedContent;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert('✅ Personalized content copied to clipboard!');
    }
  };

  // ========== AI PROMPT GENERATION FUNCTIONS ==========
  
  const handleOpenAIPromptModal = (asset) => {
    setSelectedAIPromptAsset(asset);
    setGeneratedAIContent('');
    setAIPromptInputs({ context: '', specific_details: '' });
    setShowAIPromptModal(true);
  };

  const handleGenerateWithAIPrompt = async () => {
    setAIPromptGenerating(true);
    try {
      const response = await generateWithAIPrompt(selectedAIPromptAsset.id, aiPromptInputs);
      setGeneratedAIContent(response.data.generated_content);
      alert('✅ Content generated successfully!');
    } catch (error) {
      console.error('Error generating with AI prompt:', error);
      alert('Error generating content. Please try again.');
    } finally {
      setAIPromptGenerating(false);
    }
  };

  const handleCopyGeneratedContent = async () => {
    try {
      await navigator.clipboard.writeText(generatedAIContent);
      alert('✅ Generated content copied to clipboard!');
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = generatedAIContent;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      alert('✅ Generated content copied to clipboard!');
    }
  };

  const copywritingAssets = [
    'Facebook Ad 1', 'Facebook Ad 2', 'Facebook Ad 3',
    'Email 1', 'Email 2', 'Email 3',
    'SMS 1', 'SMS 2',
    'Landing Page Copy',
    'CSR Script', 'Tech Script', 'Sales Script'
  ];

  if (loading) {
    return (
      <Layout variant="light">
        <div className="flex items-center justify-center h-64">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  if (!campaign) {
    return (
      <Layout variant="light">
        <div className="card text-center py-12">
          <p className="text-light-gray">Campaign not found</p>
          <Link to="/campaigns" className="btn-primary mt-4 inline-block">
            Back to Campaigns
          </Link>
        </div>
      </Layout>
    );
  }

  const assetAnchorId = (assetType) => `asset-card-${(assetType || '').toLowerCase().replace(/\s+/g, '-')}`;

  const jumpToAsset = (assetType) => {
    setSelectedAssetType('All');
    setStackFilter(false);
    setHighlightAsset(assetType);
    setTimeout(() => {
      document.getElementById(assetAnchorId(assetType))?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    setTimeout(() => setHighlightAsset(null), 3000);
  };

  // Get unique asset types for filtering
  const assetTypes = ['All', ...new Set(assets.map(a => a.asset_type).filter(Boolean))].sort((a, b) => {
    if (a === 'All') return -1;
    if (b === 'All') return 1;
    return a.localeCompare(b);
  });
  
  const filteredAssets = (selectedAssetType === 'All' 
    ? assets 
    : assets.filter(a => a.asset_type === selectedAssetType)
  ).filter(a => !stackFilter || !goalData?.recommended_asset_stack || goalData.recommended_asset_stack.includes(a.asset_type));

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Graphics': return Image;
      case 'Copywriting': return FileText;
      case 'Templates': return FileText;
      case 'AI/Tools': return CheckSquare;
      default: return FileText;
    }
  };

  const getRevenueBadgeClass = (badge) => {
    if (badge?.includes('Major')) return 'badge-major';
    if (badge?.includes('High')) return 'badge-high';
    if (badge?.includes('Core')) return 'badge-core';
    if (badge?.includes('Growth')) return 'badge-growth';
    if (badge?.includes('Quick')) return 'badge-quick';
    return 'badge';
  };

  return (
    <Layout variant="light">
      <div data-testid={CAMPAIGN_DETAIL.container} className="fade-in">
        <Link
          to="/campaigns"
          className="inline-flex items-center gap-2 text-construction-orange hover:underline mb-6"
        >
          <ArrowLeft size={20} />
          Back to Campaigns
        </Link>

        {/* Campaign Header */}
        <div className="card mb-8">
          <BrandAppliedBadge />
          <div className="flex flex-col md:flex-row justify-between items-start gap-4 mb-6">
            <div className="flex-1">
              <h1 className="text-4xl font-bold text-white mb-3">{campaign.campaign_name || campaign.title}</h1>
              <div className="flex flex-wrap items-center gap-3">
                <span className={`font-medium text-lg ${tradeColor(campaign.trade).text}`}>{campaign.trade}</span>
                <span className={`badge ${getRevenueBadgeClass(campaign.revenue_badge)}`}>
                  {campaign.revenue_badge}
                </span>
                <span className="text-light-gray text-sm">{campaign.urgency}</span>
                <Link to={`/campaign/${campaign.id}/launch`} data-testid="open-launch-wizard-btn"
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-lime text-black text-sm font-black hover:bg-lime/90 transition-colors">
                  🚀 Launch Wizard
                </Link>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Link
                to={`/coach?campaign=${id}`}
                data-testid="audit-campaign-btn"
                className="btn-secondary inline-flex items-center gap-2"
              >
                <Sparkles size={20} />
                Audit This Campaign
              </Link>
              <button
                onClick={() => setShowAIModal(true)}
                className="btn-primary inline-flex items-center gap-2"
              >
                <WrenchTool size={20} />
                AI Customize
              </button>
              <button
                onClick={handleDownloadAllAsZip}
                className="btn-secondary inline-flex items-center gap-2"
                disabled={assets.length === 0}
              >
                <FileArchive size={20} />
                Download All as ZIP
              </button>
              <a
                href={`/public/campaign/${id}${user?.email ? `?u=${encodeURIComponent(btoa(user.email).replace(/\+/g, '-').replace(/\//g, '_'))}` : ''}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary inline-flex items-center gap-2"
              >
                <ExternalLink size={20} />
                View Landing Page
              </a>
            </div>
          </div>

          <div className="space-y-3 border-t border-divider pt-5">
            <p className="text-light-gray leading-relaxed" data-testid="campaign-who">
              <span className="text-white font-bold">Who calls:</span>{' '}
              homeowners with <span className="text-lime font-semibold">{campaign.problem}</span>
              {campaign.homeowner_symptoms ? <> — <span className="italic">“{campaign.homeowner_symptoms}”</span></> : null}
            </p>
            <p className="text-light-gray leading-relaxed" data-testid="campaign-why">
              <span className="text-white font-bold">Why now:</span>{' '}
              {campaign.activation_trigger || campaign.pain_point}
              {campaign.seasonality ? <> · Best window: <span className="text-lime font-semibold">{campaign.seasonality}</span></> : null}
            </p>
            <p className="text-light-gray leading-relaxed" data-testid="campaign-job">
              <span className="text-white font-bold">The job:</span>{' '}
              {campaign.primary_service}
              {campaign.avg_ticket ? <> · <span className="text-lime font-semibold">{campaign.avg_ticket}</span> per job</> : null}
            </p>
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button
                onClick={() => document.getElementById('section-goal')?.scrollIntoView({ behavior: 'smooth' })}
                data-testid="hero-setup-btn"
                className="px-6 py-3 rounded-lg bg-lime text-black font-black text-sm hover:bg-lime/90 transition-colors"
              >
                Set it up ↓
              </button>
              <span className="text-sm text-gray">Goal → your numbers → launch. A few minutes.</span>
            </div>
          </div>
        </div>

        <PageFlowGuide />

        <div id="section-goal" className="scroll-mt-6">
          <CampaignGoalPanel campaignId={id} onGoalChange={(gid, gd) => setGoalData(gd)} onAssetChipClick={jumpToAsset} />
        </div>

        <div id="section-numbers" className="scroll-mt-6">
          <CampaignNumbersCard campaignId={id} onSaved={loadCampaignData} />
        </div>

        <div id="section-ai" className="scroll-mt-6">
          <AICustomizationCenter
          campaignId={id}
          goalData={goalData}
          onFilterStack={() => { setStackFilter(true); document.getElementById('assets-section')?.scrollIntoView({ behavior: 'smooth' }); }}
          onScrollToAssets={() => document.getElementById('assets-section')?.scrollIntoView({ behavior: 'smooth' })}
          onOpenAdvanced={() => setShowAIModal(true)}
          onVersionsChanged={refreshVersions}
        />
        </div>

        <div id="section-kpi" className="scroll-mt-6">
          <CampaignKPICard campaignId={id} goalData={goalData} />
        </div>

        <div id="section-tracking" className="scroll-mt-6">
          <TrackingPackagePanel campaignId={id} />
        </div>

        {/* Launch CTA + what to expect */}
        <div className="mb-8 rounded-2xl border border-lime/40 bg-lime/5 p-6" data-testid="launch-cta-banner">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">Numbers in? Launch it.</h2>
              <p className="text-sm text-light-gray">Launch now → first calls typically within 3–5 days. You approve the exact ad before anything goes live.</p>
            </div>
            <Link to={`/campaign/${id}/launch`} data-testid="launch-cta-btn"
              className="px-8 py-4 rounded-xl bg-lime text-black font-black text-lg hover:bg-lime/90 transition-colors">
              Launch Wizard →
            </Link>
          </div>
          <div className="grid sm:grid-cols-3 gap-3 mt-5">
            {[['$40/day', '~4–6 leads/wk', 'Test the waters'], ['$90/day', '~10–16 leads/wk', 'Grow steadily'], ['$175/day', '~20–30 leads/wk', 'Go aggressive']].map(([b, l, n]) => (
              <div key={b} className="rounded-xl bg-black/40 border border-divider p-4 text-center">
                <p className="text-lime font-black text-xl">{b}</p>
                <p className="text-white text-sm font-bold">{l}</p>
                <p className="text-xs text-gray">{n}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray mt-4 flex items-center gap-1.5">
            The number to watch after launch: Cost Per Lead
            <HelpTooltip text="Cost Per Lead — what you pay for each real call or form. Aim under $50–$150 for HVAC, $30–$80 for plumbing and electrical. If it's higher after week one, the AI Advisor will tell you what to fix." testId="help-cta-cpl" />
          </p>
        </div>

        {/* Filter Section: All Assets + Filter by Activation */}
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <h2 className="text-2xl font-bold text-construction-orange">All Assets</h2>
          
          <div className="flex items-center gap-3">
            <label className="text-light-gray font-medium">Filter by Activation:</label>
            <select
              value={selectedAssetType}
              onChange={(e) => setSelectedAssetType(e.target.value)}
              className="bg-slate-blue text-white px-4 py-2 rounded-lg border-2 border-lime focus:outline-none focus:border-lime transition-colors min-w-[250px]"
            >
              {assetTypes.map((type) => (
                <option key={type} value={type}>
                  {type === 'All' ? 'All Activation Types' : type}
                </option>
              ))}
            </select>
            {selectedAssetType !== 'All' && (
              <button
                onClick={() => setSelectedAssetType('All')}
                className="text-lime hover:text-lime/80 text-sm font-medium"
              >
                Clear
              </button>
            )}
          </div>

          {/* Asset Usage Guide Button */}
          {usageGuide && (
            <button
              onClick={() => setShowUsageGuide(true)}
              className="bg-lime text-black font-bold px-8 py-3 rounded-2xl hover:bg-lime/90 transition-all inline-flex items-center gap-2 text-lg shadow-lg ml-auto"
            >
              <BookOpen size={20} />
              Asset Usage Guide
            </button>
          )}
        </div>

        {/* Assets Grid */}
        <div id="assets-section">
          <h2 className="text-2xl font-bold text-white mb-4">
            Marketing Assets ({filteredAssets.length})
          </h2>
          {stackFilter && goalData && (
            <div className="mb-4 p-3 rounded-lg bg-lime/10 border border-lime/40 flex items-center justify-between flex-wrap gap-2" data-testid="stack-filter-banner">
              <p className="text-sm text-lime font-semibold">
                Showing your recommended stack for “{goalData.label}” — these are the assets to launch first.
              </p>
              <button onClick={() => setStackFilter(false)} data-testid="stack-filter-clear" className="text-xs text-white hover:underline">
                Show all assets
              </button>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAssets.map((asset) => {
              const Icon = getCategoryIcon(asset.asset_category);
              const v = versions[asset.id];
              const status = v?.status || 'ready';
              const previewContent = activeContentFor(asset);
              return (
                <div
                  key={asset.id}
                  id={assetAnchorId(asset.asset_type)}
                  data-testid={CAMPAIGN_DETAIL.assetCard}
                  className={`card ${highlightAsset === asset.asset_type ? 'ring-2 ring-lime shadow-lg shadow-lime/20' : ''}`}
                >
                  <div className="flex items-start gap-3 mb-4">
                    <div className="p-2 bg-slate-blue rounded-lg text-construction-orange">
                      <Icon size={20} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-white text-lg mb-1">{asset.asset_type}</h3>
                      <p className="text-xs text-muted">{asset.asset_category}</p>
                    </div>
                    <span
                      data-testid="asset-card-status"
                      className={`text-xs px-2 py-1 rounded ${STATUS_STYLES[status] || STATUS_STYLES.ready}`}
                    >
                      {STATUS_LABELS[status] || 'Ready'}
                    </span>
                  </div>

                  {previewContent && (
                    <div className="mb-4">
                      <p className="text-sm text-light-gray line-clamp-4">
                        {previewContent.substring(0, 200)}...
                      </p>
                      {v && v.active_version !== 'master' && (
                        <p className="text-[13px] text-lime mt-1" data-testid="asset-card-version-hint">
                          {v.active_version === 'edited' ? '✎ Your edited version' : '✨ Your AI draft'}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Tool Handoff Buttons */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <button
                      onClick={() => handleCopyToClipboard(asset)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-blue hover:bg-dark-steel text-light-gray text-xs rounded-lg transition-colors"
                      title="Copy to clipboard"
                    >
                      <Copy size={14} />
                      Copy
                    </button>

                    <RewriteMenu
                      assetId={asset.id}
                      onDone={refreshVersions}
                      onError={(m) => alert(m)}
                    />
                    
                    {/* Built-in AI Generation for AI Prompt assets */}
                    {(asset.asset_type?.includes('AI Prompt') || asset.asset_type?.toLowerCase().includes('prompt')) && (
                      <button
                        onClick={() => handleOpenAIPromptModal(asset)}
                        className="inline-flex items-center gap-1 px-4 py-2 bg-lime text-black text-sm font-bold rounded-lg transition-all hover:bg-lime/90 shadow-md"
                        title="Generate content using AI (built-in)"
                      >
                        <Sparkles size={16} />
                        Generate with AI
                      </button>
                    )}
                    
                    {(asset.asset_name?.includes('ChatGPT') || asset.asset_name?.includes('AI Prompt')) && (
                      <button
                        onClick={() => handleOpenInChatGPT(asset)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600/20 hover:bg-green-600/30 text-green-400 text-xs rounded-lg transition-colors"
                        title="Open in ChatGPT"
                      >
                        <ExternalLinkIcon size={14} />
                        ChatGPT
                      </button>
                    )}
                    
                    {asset.asset_name?.includes('Claude') && (
                      <button
                        onClick={() => handleOpenInClaude(asset)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-purple-600/20 hover:bg-purple-600/30 text-purple-400 text-xs rounded-lg transition-colors"
                        title="Open in Claude"
                      >
                        <ExternalLinkIcon size={14} />
                        Claude
                      </button>
                    )}
                    
                    {(asset.category === 'Graphics' || asset.category === 'Print' || asset.asset_name?.includes('Canva')) && (
                      <button
                        onClick={() => handleOpenInCanva(asset)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600/20 hover:bg-blue-600/30 text-blue-400 text-xs rounded-lg transition-colors"
                        title="Open in Canva"
                      >
                        <ExternalLinkIcon size={14} />
                        Canva
                      </button>
                    )}
                    
                    {(asset.asset_name?.includes('Email') && !asset.asset_name?.includes('Script')) && (
                      <button
                        onClick={() => handleSendToEmail(asset)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 bg-orange/20 hover:bg-orange/30 text-orange text-xs rounded-lg transition-colors"
                        title="Send to Email"
                      >
                        <Mail size={14} />
                        Email
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-3">
                    <Link
                      to={`/asset/${asset.id}`}
                      className="text-construction-orange text-sm hover:underline"
                    >
                      View & Edit →
                    </Link>
                    <button
                      onClick={() => handleDownloadAsset(asset)}
                      className="inline-flex items-center gap-1 text-light-gray text-sm hover:text-construction-orange"
                    >
                      <Download size={16} />
                      Download
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredAssets.length === 0 && (
            <div className="card text-center py-12">
              <p className="text-light-gray">No assets found in this category.</p>
            </div>
          )}
        </div>

        {/* Deployment Checklist Info */}
        <div className="mt-8 card">
          <div className="flex items-start gap-4">
            <CheckSquare className="text-construction-orange mt-1" size={32} />
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-white mb-2">Campaign Deployment Checklist</h2>
              <p className="text-light-gray mb-4">
                Each campaign includes a complete activation checklist to help you deploy successfully. 
                Download the checklist template and track your progress offline.
              </p>
              <button className="btn-secondary inline-flex items-center gap-2">
                <Download size={20} />
                Download Checklist Template
              </button>
            </div>
          </div>
        </div>

        {/* AI Modal */}
        {showAIModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <WrenchTool className="text-construction-orange" size={28} />
                AI Campaign Customization
              </h2>
              <p className="text-light-gray mb-6">
                Select assets to customize with AI. The AI will personalize the campaign copy with your 
                brand voice, local market, and specific services based on your company settings.
              </p>

              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold text-white">Select Assets to Customize</h3>
                  <button
                    onClick={() => setSelectedAssetTypes(
                      selectedAssetTypes.length === copywritingAssets.length ? [] : copywritingAssets
                    )}
                    className="text-sm text-construction-orange hover:underline"
                  >
                    {selectedAssetTypes.length === copywritingAssets.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {copywritingAssets.map((assetType) => (
                    <label
                      key={assetType}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                        selectedAssetTypes.includes(assetType)
                          ? 'bg-construction-orange/20 border-2 border-construction-orange'
                          : 'bg-slate-blue border-2 border-divider hover:border-construction-orange/50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedAssetTypes.includes(assetType)}
                        onChange={() => toggleAssetType(assetType)}
                        className="w-5 h-5 rounded"
                      />
                      <span className="text-white text-sm">{assetType}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="bg-slate-blue p-4 rounded-lg mb-6">
                <p className="text-sm text-light-gray">
                  <strong className="text-white">Note:</strong> AI generation typically takes 5-10 seconds per asset. 
                  Selected assets: {selectedAssetTypes.length}
                </p>
              </div>

              <div className="flex gap-4">
                <button
                  onClick={handleAIGeneration}
                  disabled={aiGenerating || selectedAssetTypes.length === 0}
                  className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {aiGenerating ? (
                    <>
                      <div className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <WrenchTool className="inline mr-2" size={20} />
                      Customize {selectedAssetTypes.length} Asset{selectedAssetTypes.length !== 1 ? 's' : ''}
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowAIModal(false);
                    setSelectedAssetTypes([]);
                  }}
                  disabled={aiGenerating}
                  className="btn-secondary flex-1 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Usage Guide Modal */}
        {showUsageGuide && usageGuide && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-card-bg border border-divider rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Modal Header */}
              <div className="bg-lime p-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <BookOpen size={28} className="text-black" />
                  <h2 className="text-2xl font-bold text-black">Asset Usage Guide</h2>
                </div>
                <button
                  onClick={() => setShowUsageGuide(false)}
                  className="text-black hover:bg-black/10 rounded-lg p-2 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto p-6">
                <div className="prose prose-invert max-w-none">
                  <div className="bg-near-black border border-lime/20 rounded-lg p-6 mb-6">
                    <p className="text-lime font-semibold mb-2">📚 How to Use This Guide</p>
                    <p className="text-light-gray text-sm">
                      This comprehensive guide explains how to deploy each of the 29 marketing assets in this campaign. 
                      Follow the recommendations for timing, implementation, and best practices to maximize your ROI.
                    </p>
                  </div>
                  <div className="text-light-gray whitespace-pre-wrap leading-relaxed">
                    {usageGuide.content}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="border-t border-divider p-4 flex justify-between items-center bg-near-black">
                <div className="text-sm text-muted">
                  Total Assets: 29 | Campaign: {campaign?.campaign_name}
                </div>
                <button
                  onClick={() => setShowUsageGuide(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CampaignDetail;
